/**
 * Should the split's secondary pane render right now?
 *
 * False when there is no pane, false below the nav breakpoint (a phone has one
 * column; the pane is `display:none` there and a "visible" answer let
 * navigation focus a pane nobody could see), and false when the pane holds a
 * tab belonging to a different app than the installed one that is running —
 * the workspace store is shared with ordinary browser tabs, so a saved split
 * can point anywhere.
 *
 * A read-side derivation on purpose. Closing the pane in the store would mutate
 * state the browser tab shares, so launching an app would silently dismantle a
 * split the user set up there. Same treatment `pinsSidebarCollapsed` gives the
 * sidebar-width preference.
 *
 * Both the layout and the navigation coordinator consult this: they must agree,
 * or clicks route to a pane nobody can see.
 */
import { useInstalledApp } from '@shared/app-identity/useInstalledApp';
import { useViewport } from '@shared/hooks/useViewport';

import { belongsToApp } from '../appScope';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';

/** True when the secondary pane exists and belongs to the running app. */
export function useSecondaryPaneVisible(): boolean {
  const installedApp = useInstalledApp();
  const viewport = useViewport();
  const secondaryActiveTabId = useWorkspaceStore(s => s.secondaryActiveTabId);
  const tabs = useWorkspaceStore(s => s.tabs);

  if (viewport === 'phone') return false;
  if (!secondaryActiveTabId) return false;
  const tab = tabs.find(t => t.id === secondaryActiveTabId);
  if (!tab) return false;
  return belongsToApp(tab.path, installedApp);
}
